<template>
  <div class="modal fade fixed-end story-history-sheet" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-dialog-vertical" role="document">
      <div class="modal-content">
        <div class="history-header">
          <div class="history-header-text">
            <h2>Version history</h2>
            <p v-if="currentStory">#{{ currentStory.id }} &middot; {{ currentStory.title }}</p>
          </div>
          <button type="button" class="history-close" data-bs-dismiss="modal" aria-label="Close">
            <i class="far fa-times"></i>
          </button>
        </div>

        <div v-if="error" class="history-error">
          <p>Unable to load the history for this user story.</p>
          <button type="button" class="btn btn-outline-primary btn-sm" @click="loadHistory">Try again</button>
        </div>

        <div v-else class="history-body">
          <version-list
          :versions="versions"
          :selected="from"
          :loading="loading"
          @select="selectVersion"
          />

          <diff-panel
          :versions="versions"
          :from.sync="from"
          :to.sync="to"
          :mode="mode"
          :collapse="collapse"
          :loading="loading"
          @update:mode="setMode"
          @update:collapse="setCollapse"
          >
          <template #footer>
            <div class="history-footer">
              <p>
                <template v-if="canRestore">Restoring reverts the story to v{{ from }}. This is saved as a new version, and nothing is lost.</template>
                <template v-else>Select an earlier version to restore it.</template>
              </p>
              <button type="button" class="btn btn-outline-secondary" :disabled="loading || !fromVersion" @click="copyText">
                <i class="far fa-copy"></i> Copy text
              </button>
              <button type="button" class="btn btn-primary" :disabled="!canRestore || restoring" @click="restore">
                <i class="far" :class="restoring ? 'fa-spinner fa-spin' : 'fa-history'"></i>
                {{ canRestore ? `Restore v${from}` : 'Restore' }}
              </button>
            </div>
          </template>
        </diff-panel>
      </div>
    </div>
  </div>
</div>
</template>

<script>
import { http } from 'utils'
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
import { CModal, toast } from '@chameleon/chameleon-vue'
import { ADD_USERSTORY } from 'vx/mutations'
import { buildVersions, versionText } from 'utils/story-history'
import VersionList from './history/VersionList.vue'
import DiffPanel from './history/DiffPanel.vue'

const MODE_KEY = 'userstory-history.mode'
const COLLAPSE_KEY = 'userstory-history.collapse'

const readPreference = (key, fallback) => {
  try {
    const value = window.localStorage.getItem(key)
    return value === null ? fallback : JSON.parse(value)
  } catch (e) {
    return fallback
  }
}

const writePreference = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch (e) {}
}

export default {
  extends: CModal,

  components: {
    VersionList,
    DiffPanel
  },

  data() {
    return {
      storyId: null,
      entries: [],
      from: null,
      to: null,
      mode: 'inline',
      collapse: true,
      loading: false,
      error: false,
      restoring: false
    }
  },

  computed: {
    ...mapState('epics', ['epics']),
    ...mapGetters('users', ['getById']),

    currentStory () {
      for (const epic of this.epics) {
        const story = (epic.stories || []).find(s => s.id == this.storyId)
        if (story) return story
      }
      return null
    },

    creator () {
      const user = this.currentStory && this.getById(this.currentStory.creator_id)

      return user ? { id: user.id, name: user.name || `${user.first_name} ${user.last_name}` } : null
    },

    versions () {
      if (this.loading || !this.currentStory) return []

      return buildVersions(this.entries)
    },

    fromVersion () {
      return this.versions.find(version => version.number == this.from)
    },

    canRestore () {
      return !this.loading && !!this.fromVersion && !this.fromVersion.current
    }
  },

  mounted() {
    this.addEventListener('open', () => {
      this.mode = readPreference(MODE_KEY, 'inline')
      this.collapse = readPreference(COLLAPSE_KEY, true)

      if (!this.$store.state.users.users.length) this.getUsers()

      this.loadHistory()
    })
  },

  methods: {
    ...mapMutations('epics', [ADD_USERSTORY]),
    ...mapActions('users', ['getUsers']),

    loadHistory() {
      this.loading = true
      this.error = false

      const { id, user_story_document_id } = this.currentStory

      return http.get(`/user-story-document/${user_story_document_id}/stories/${id}/history`).then((response) => {
        this.entries = response.data.data
        this.loading = false

        const count = this.versions.length
        this.from = Math.max(count - 1, 1)
        this.to = count
      }).catch(() => {
        this.loading = false
        this.error = true
      })
    },

    selectVersion(version) {
      const current = this.versions.length

      this.from = version.current ? Math.max(current - 1, 1) : version.number
      this.to = current
    },

    setMode(mode) {
      this.mode = mode
      writePreference(MODE_KEY, mode)
    },

    setCollapse(collapse) {
      this.collapse = collapse
      writePreference(COLLAPSE_KEY, collapse)
    },

    restore() {
      const number = this.from

      this.restoring = true

      const { id, user_story_document_id } = this.currentStory

      return http.post(`/user-story-document/${user_story_document_id}/stories/${id}/history/revert`, {
        ...this.fromVersion.state
      }).then((response) => {
        this[ADD_USERSTORY]({ ...this.currentStory, ...response.data })

        return this.loadHistory().then(() => {
          toast(`Restored v${number}`, 'success')
        })
      }).catch((error) => {
        toast((error.response && error.response.data && error.response.data.message) || `Unable to restore v${number}`, 'danger')
      }).finally(() => {
        this.restoring = false
      })
    },

    copyText() {
      const version = this.fromVersion

      navigator.clipboard.writeText(versionText(version)).then(() => {
        toast(`Copied v${version.number} text`, 'success')
      }).catch(() => {
        toast('Unable to copy to the clipboard', 'danger')
      })
    }
  }
}
</script>
